import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ScrollText } from "lucide-react";

const ACTION_STYLES = {
  create: "bg-[#ECF8F1] text-[#1A5633]", update: "bg-[#FBEAED] text-[#8A0822]",
  delete: "bg-[#FDF2F4] text-[#8A0822]", publish: "bg-[#ECF8F1] text-[#1A5633]",
  review: "bg-[#FFF8EC] text-[#824D0E]", import: "bg-[#FBEAED] text-[#8A0822]",
};

export default function AdminAudit() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (entity) params.set("entity_type", entity);
    if (action) params.set("action", action);
    api.get(`/audit-log?${params}`).then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(load, [entity, action]);

  const exportCsv = () => {
    const rows = [["timestamp", "user", "role", "action", "entity_type", "entity_id", "summary"]];
    logs.forEach((l) => rows.push([l.timestamp, l.user_name, l.user_role, l.action, l.entity_type, l.entity_id, l.summary]));
    const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "audit_log.csv"; a.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">Audit Log</h1>
          <p className="text-[#5A687C] mt-1">Append-only history of every change — who, what, when, and before/after values.</p>
        </div>
        <button data-testid="export-audit-btn" onClick={exportCsv} className="inline-flex items-center gap-1.5 bg-[#F4F6FA] hover:bg-[#DCE2EA] border border-[#DCE2EA] rounded-full px-4 py-2 text-sm font-medium">Export CSV</button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select data-testid="audit-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)} className="px-4 py-2 rounded-full border border-[#DCE2EA] text-sm bg-white">
          <option value="">All entities</option>
          {["workstream", "finding", "mitigation", "task", "milestone", "raid", "budget", "user", "approval", "intake", "config", "comment"].map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} className="px-4 py-2 rounded-full border border-[#DCE2EA] text-sm bg-white">
          <option value="">All actions</option>
          {["create", "update", "delete", "publish", "review", "import"].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="bg-[#F4F6FA] text-[#5A687C] text-xs font-bold uppercase tracking-wider">
            <th className="text-left px-4 py-3">When</th><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Action</th>
            <th className="text-left px-4 py-3">Entity</th><th className="text-left px-4 py-3">Summary</th><th className="text-left px-4 py-3">Before → After</th>
          </tr></thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-[#DCE2EA]/60 hover:bg-[#F4F6FA]/60 align-top">
                <td className="px-4 py-3 text-[#98A2AF] text-xs whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                <td className="px-4 py-3"><div className="font-semibold text-[#1F2733]">{l.user_name}</div><div className="text-[11px] text-[#98A2AF]">{l.user_role}</div></td>
                <td className="px-4 py-3"><span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full ${ACTION_STYLES[l.action] || "bg-[#F1F3F6] text-[#4A525D]"}`}>{l.action}</span></td>
                <td className="px-4 py-3 text-[#5A687C]">{l.entity_type}</td>
                <td className="px-4 py-3 text-[#1F2733] max-w-xs">{l.summary}</td>
                <td className="px-4 py-3 text-[11px] text-[#98A2AF] max-w-xs">
                  {l.before_value && <div className="text-[#8A0822]">− {JSON.stringify(l.before_value)}</div>}
                  {l.after_value && <div className="text-[#1A5633]">+ {JSON.stringify(l.after_value)}</div>}
                </td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#5A687C]">No audit entries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
